'use client'

// =============================================================================
// AppShell — OS layout responsive
//
//  Desktop (≥ 900px) :
//  ┌────────────────────────────────────────────────────┐
//  │               TOPBAR (44px)                        │
//  ├──────────┬─────────────────────────┬───────────────┤
//  │ SIDEBAR  │      CENTER             │  AI PANEL     │
//  │ (240px)  │   (flex grow)           │ (300px, opt)  │
//  ├──────────┴─────────────────────────┴───────────────┤
//  │             FOOTER REALTIME (36px)                 │
//  └────────────────────────────────────────────────────┘
//
//  Mobile (< 900px) : une seule colonne. La sidebar devient un TIROIR (drawer)
//  ouvert par le ☰ de la topbar ; le panneau IA s'ouvre en superposition.
// =============================================================================

import { useState, useEffect, useCallback } from 'react'
import { useSession }    from 'next-auth/react'
import Topbar            from './Topbar'
import Sidebar           from './Sidebar'
import RightPanel        from './RightPanel'
import Footer            from './FooterRealtime'

interface AiArticle {
  id: string; titre: string; url: string
  urlImage?: string | null
  source: { nom: string; logoUrl?: string | null; url?: string; language?: string }
}
interface AiNews {
  region: string; summary: string; articles: AiArticle[]
  fromCache: boolean; timestamp: string
}

const API_BASE = (process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001').replace(/\/$/, '')

// Breakpoint mobile — sous cette largeur, sidebar + panneau IA passent en overlay.
function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [breakpoint])
  return isMobile
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  // Panneau IA (avec l'explorateur / éditeur) : ouvert par défaut sur ordinateur ; le choix de l'usager est retenu.
  const [rightOpen,    setRightOpen]    = useState(false)
  const [sidebarOpen,  setSidebarOpen]  = useState(false)   // tiroir mobile
  const [region,       setRegion]       = useState('06')
  const [news,         setNews]         = useState<AiNews | null>(null)
  const [newsLoading,  setNewsLoading]  = useState(false)
  const { data: session }               = useSession()
  const isMobile                        = useIsMobile()

  const userLang = (session?.user as any)?.profile?.language ?? 'fr'

  const fetchNews = useCallback(async (regionCode: string) => {
    setNewsLoading(true)
    try {
      const token = (session?.user as any)?.accessToken ?? ''
      const res   = await fetch(`${API_BASE}/news/region/${regionCode}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal:  AbortSignal.timeout(8000),
      })
      if (res.ok) setNews(await res.json())
    } catch {
      // non-critical — ticker stays empty
    } finally {
      setNewsLoading(false)
    }
  }, [session])

  useEffect(() => { fetchNews(region) }, [region, fetchNews])
  useEffect(() => {
    const id = setInterval(() => fetchNews(region), 5 * 60 * 1000)
    return () => clearInterval(id)
  }, [region, fetchNews])

  useEffect(() => {
    if (window.innerWidth < 900) return
    let saved: string | null = null
    try { saved = localStorage.getItem('ab-right-open') } catch { /* stockage indisponible */ }
    setRightOpen(saved !== '0')
  }, [])
  // Le choix est écrit au clic (pas dans un effet : le double rendu de React en dev relirait la valeur qu'on vient d'écrire).
  const toggleRight = useCallback(() => {
    setRightOpen(o => {
      const next = !o
      if (window.innerWidth >= 900) { try { localStorage.setItem('ab-right-open', next ? '1' : '0') } catch { /* idem */ } }
      return next
    })
  }, [])

  // Fermer le tiroir mobile quand on repasse en desktop.
  useEffect(() => { if (!isMobile) setSidebarOpen(false) }, [isMobile])

  // ── Layout mobile : une colonne, sidebar + IA en overlay ──────────────────
  if (isMobile) {
    return (
      <div style={{
        display: 'grid', gridTemplateRows: '44px 1fr 36px',
        height: '100dvh', width: '100%', overflow: 'hidden', background: 'var(--bg-deep)',
      }}>
        <div style={{ overflow: 'hidden' }}>
          <Topbar
            onToggleRight={() => setRightOpen(o => !o)}
            rightOpen={rightOpen}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            showMenu
            news={news} newsLoading={newsLoading}
            region={region} onRegionChange={setRegion}
            userLanguage={userLang}
          />
        </div>

        <main className="hud-bg" style={{ overflow: 'auto', position: 'relative', minWidth: 0, WebkitOverflowScrolling: 'touch' }}>
          {children}
        </main>

        <div style={{ overflow: 'hidden' }}><Footer /></div>

        {/* Tiroir Sidebar (overlay) */}
        {sidebarOpen && (
          <>
            <div onClick={() => setSidebarOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 600, backdropFilter: 'blur(2px)' }} />
            <div style={{
              position: 'fixed', top: 0, left: 0, bottom: 0, width: 'min(82vw, 300px)', zIndex: 601,
              background: 'var(--bg-deep)', borderRight: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '4px 0 40px rgba(0,0,0,0.6)', overflow: 'auto',
              animation: 'drawerIn 0.22s cubic-bezier(0.4,0,0.2,1)',
            }}>
              <div onClick={() => setSidebarOpen(false)}><Sidebar /></div>
            </div>
          </>
        )}

        {/* Panneau IA (overlay) */}
        {rightOpen && (
          <>
            <div onClick={() => setRightOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 600, backdropFilter: 'blur(2px)' }} />
            <div style={{
              position: 'fixed', top: 44, right: 0, bottom: 36, width: 'min(88vw, 340px)', zIndex: 601,
              background: 'var(--bg-deep)', borderLeft: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '-4px 0 40px rgba(0,0,0,0.6)', overflow: 'auto',
              animation: 'drawerInRight 0.22s cubic-bezier(0.4,0,0.2,1)',
            }}>
              <RightPanel />
            </div>
          </>
        )}

        <style>{`
          @keyframes drawerIn { from { transform: translateX(-100%) } to { transform: translateX(0) } }
          @keyframes drawerInRight { from { transform: translateX(100%) } to { transform: translateX(0) } }
        `}</style>
      </div>
    )
  }

  // ── Layout desktop : grille 5 panneaux ────────────────────────────────────
  return (
    <div style={{
      display:             'grid',
      gridTemplateRows:    '44px 1fr 36px',
      gridTemplateColumns: `240px 1fr ${rightOpen ? '340px' : '0px'}`,
      gridTemplateAreas:   `
        "topbar  topbar topbar"
        "sidebar center right"
        "footer  footer footer"
      `,
      height:     '100dvh',
      width:      '100%',
      overflow:   'hidden',
      background: 'var(--bg-deep)',
      transition: 'grid-template-columns 0.25s cubic-bezier(0.4,0,0.2,1)',
    }}>

      <div style={{ gridArea: 'topbar', overflow: 'hidden' }}>
        <Topbar
          onToggleRight={toggleRight}
          rightOpen={rightOpen}
          news={news}
          newsLoading={newsLoading}
          region={region}
          onRegionChange={setRegion}
          userLanguage={userLang}
        />
      </div>

      <div style={{ gridArea: 'sidebar', overflow: 'hidden', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
        <Sidebar />
      </div>

      <main className="hud-bg" style={{ gridArea: 'center', overflow: 'auto', position: 'relative', minWidth: 0 }}>
        {children}
      </main>

      <div style={{
        gridArea:   'right',
        overflow:   'hidden',
        borderLeft: rightOpen ? '1px solid rgba(255,255,255,0.05)' : 'none',
        width:      rightOpen ? 340 : 0,
        transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {rightOpen && <RightPanel />}
      </div>

      <div style={{ gridArea: 'footer', overflow: 'hidden' }}>
        <Footer />
      </div>
    </div>
  )
}
